// ============================================================
// Rotas do módulo Clínica (/clinica) — terceiro módulo, irmão de
// Paciente e Médico. Aditivo: não altera as rotas existentes.
// Fase 2: RBAC por perfil (Admin vs Atendimento) via meta.role + guard.
// ============================================================
import store from "../state/store";
import VueRouterMultiguard from "vue-router-multiguard";

const ClinicAppLayout = () => import("../layouts/ClinicApp.vue");
const view = name => () => import(`../views/clinica/${name}.vue`);

// 1) Exige login real (mesma regra do app: redireciona ao Login preservando destino).
const authMiddleware = (to, from, next) => {
  store.getters["auth/loggedIn"]
    ? next()
    : next({ name: "Login", query: { redirectFrom: to.fullPath } });
};

// 2) Garante a sessão da clínica carregada, exige que o usuário seja staff e
//    aplica a permissão da clínica ativa (meta.permission = [área, nível],
//    US-C11).
//    Obs.: staff pode ter account_type 'patient' (secretária pura) — por isso
//    o gate é o membership (clinic_staff), não o account_type.
const clinicGuard = async (to, from, next) => {
  if (!store.state.clinica || !store.state.clinica.initialized) {
    try {
      await store.dispatch("clinica/init");
    } catch (e) {
      // falha ao carregar sessão → trata como não-staff abaixo
    }
  }
  if (!store.getters["clinica/isStaff"]) {
    return next({ name: "Site" });
  }
  // US-C12: com senha provisória, nada do módulo antes da troca.
  const user = store.state.auth && store.state.auth.loggedUser;
  if (user && user.must_change_password) {
    return next({ name: "ChangePassword" });
  }
  const required = to.meta && to.meta.permission;
  if (required && !store.getters["clinica/canAccess"](required)) {
    return next({ name: "ClinicDashboard", query: { denied: to.name } });
  }
  next();
};

// helper: monta uma rota do módulo com layout + permissão + guards (auth → clínica)
const route = (path, name, viewName, permission) => ({
  path,
  name,
  component: view(viewName),
  meta: { layout: ClinicAppLayout, permission },
  beforeEnter: VueRouterMultiguard([authMiddleware, clinicGuard])
});

const VIEW = level => area => [area, level];
const canView = VIEW("view");
const canEdit = VIEW("edit");

const clinicaRoutes = [
  // Aceite público de convite (staff/médico) — sem login, sem shell da clínica.
  {
    path: "/clinica/convite/:token",
    name: "ClinicInviteAccept",
    component: view("ClinicInviteAccept"),
    meta: { kind: "staff" }
  },
  {
    path: "/clinica/convite-medico/:token",
    name: "ClinicDoctorInviteAccept",
    component: view("ClinicInviteAccept"),
    meta: { kind: "doctor" }
  },
  route("/clinica", "ClinicDashboard", "ClinicDashboard"),
  route(
    "/clinica/pacientes",
    "ClinicPatients",
    "ClinicPatients",
    canView("pacientes")
  ),
  route(
    "/clinica/pacientes/:id",
    "ClinicPatientProfile",
    "ClinicPatientProfile",
    canView("pacientes")
  ),
  route(
    "/clinica/acompanhamento/sem-retorno",
    "ClinicNoReturn",
    "ClinicNoReturn",
    canView("acompanhamento")
  ),
  route(
    "/clinica/acompanhamento/oportunidades",
    "ClinicOpportunities",
    "ClinicOpportunities",
    canView("acompanhamento")
  ),
  route(
    "/clinica/acompanhamento/campanhas",
    "ClinicCampaigns",
    "ClinicCampaigns",
    canView("acompanhamento")
  ),
  route(
    "/clinica/agenda",
    "ClinicSchedule",
    "ClinicSchedule",
    canView("agenda")
  ),
  route(
    "/clinica/confirmacoes",
    "ClinicConfirmations",
    "ClinicConfirmations",
    canView("confirmacoes")
  ),
  route(
    "/clinica/reagendamentos",
    "ClinicReschedules",
    "ClinicReschedules",
    canView("reagendamentos")
  ),
  route(
    "/clinica/financeiro",
    "ClinicFinanceDashboard",
    "ClinicFinanceDashboard",
    canView("financeiro")
  ),
  route(
    "/clinica/financeiro/recebimentos",
    "ClinicFinanceReceivables",
    "ClinicFinanceReceivables",
    canView("financeiro")
  ),
  route(
    "/clinica/financeiro/repasses",
    "ClinicFinanceTransfers",
    "ClinicFinanceTransfers",
    canView("financeiro")
  ),
  route(
    "/clinica/financeiro/extrato",
    "ClinicFinanceStatement",
    "ClinicFinanceStatement",
    canView("financeiro")
  ),
  route(
    "/clinica/medicos",
    "ClinicDoctors",
    "ClinicDoctors",
    canView("medicos")
  ),
  route(
    "/clinica/medicos/novo",
    "ClinicDoctorNew",
    "ClinicDoctorNew",
    canEdit("medicos")
  ),
  route(
    "/clinica/medicos/:id",
    "ClinicDoctorProfile",
    "ClinicDoctorProfile",
    canView("medicos")
  ),
  // agenda do médico é acessível ao Atendimento (README seção 4, item 17)
  route(
    "/clinica/medicos/:id/agenda",
    "ClinicDoctorSchedule",
    "ClinicDoctorSchedule",
    canView("agenda")
  ),
  route(
    "/clinica/medicos/:id/horarios",
    "ClinicDoctorHours",
    "ClinicDoctorHours",
    canView("medicos")
  ),
  route("/clinica/config", "ClinicConfig", "ClinicConfig", canView("config"))
];

export default clinicaRoutes;
